import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { useParish, type Parish } from "../lib/parishContext";
import {
  TOGGLEABLE_KINDS,
  useAttentionPreferences,
} from "../lib/useAttentionPreferences";
import { accentContrastFg, isHexColor } from "../lib/theme";
import { supabase } from "../lib/supabaseClient";
import { apiDelete, apiPatch, apiPost, apiPostFormData } from "../lib/api";
import { resolveApiUrl } from "../lib/url";

const ALLOWED_LOGO_MIMES = "image/png,image/svg+xml,image/jpeg";
const MAX_LOGO_BYTES = 500_000;

export default function Settings() {
  const session = useSession();
  const { parish, refresh } = useParish();
  const navigate = useNavigate();
  const { prefs: attentionPrefs, set: setAttentionPref } = useAttentionPreferences();
  const [displayName, setDisplayName] = useState(parish.display_name);
  const [accentColor, setAccentColor] = useState(parish.accent_color);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; message: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Sync local form state when the parish refreshes (initial load,
  // post-logo-upload, etc.).
  useEffect(() => {
    setDisplayName(parish.display_name);
    setAccentColor(parish.accent_color);
  }, [parish.display_name, parish.accent_color]);

  // The settings page is on the Sacristy theme; changing the accent
  // here updates a local sample-button preview (rendered with inline
  // styles below) rather than mutating document-level CSS. The
  // saved value applies to volunteer-facing surfaces only.
  const onAccentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAccentColor(e.target.value);
  };

  // Auto-dismiss toast after a few seconds.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const save = async () => {
    setSaving(true);
    try {
      await apiPatch<{ parish: Parish }>("/parish/settings", {
        display_name: displayName.trim(),
        accent_color: accentColor,
      });
      await refresh();
      setToast({ kind: "ok", message: "Saved" });
    } catch (err) {
      setToast({ kind: "err", message: errorMessage(err, "Couldn't save settings") });
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // allow re-picking the same file later
    if (file.size > MAX_LOGO_BYTES) {
      setToast({
        kind: "err",
        message: `Logo is too large (${Math.round(file.size / 1024)} KB). Max 500 KB.`,
      });
      return;
    }
    const form = new FormData();
    form.append("logo", file);
    try {
      await apiPostFormData<{ parish: Parish }>("/parish/logo", form);
      await refresh();
      setToast({ kind: "ok", message: "Logo uploaded" });
    } catch (err) {
      setToast({ kind: "err", message: errorMessage(err, "Couldn't upload logo") });
    }
  };

  // Public schedule page (/s/<capability-slug>). The slug only ever
  // arrives on the authenticated settings payload; the public branding
  // endpoint omits it deliberately.
  const scheduleUrl = parish.public_schedule_slug
    ? `${window.location.origin}/s/${parish.public_schedule_slug}`
    : null;

  const copyScheduleUrl = async () => {
    if (!scheduleUrl) return;
    try {
      await navigator.clipboard.writeText(scheduleUrl);
      setToast({ kind: "ok", message: "Link copied" });
    } catch {
      setToast({ kind: "err", message: "Couldn't copy — select the link and copy manually." });
    }
  };

  const regenerateScheduleLink = async () => {
    if (
      !window.confirm(
        "Generate a new schedule link? The current link will stop working immediately, and anyone you've already shared it with will need the new one.",
      )
    ) {
      return;
    }
    setRegenerating(true);
    try {
      await apiPost<{ parish: Parish }>("/parish/schedule-link/regenerate", {});
      await refresh();
      setToast({ kind: "ok", message: "New link generated — the old one no longer works." });
    } catch (err) {
      setToast({ kind: "err", message: errorMessage(err, "Couldn't generate a new link") });
    } finally {
      setRegenerating(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the parish logo? You can always upload a new one.")) return;
    try {
      await apiDelete<{ parish: Parish }>("/parish/logo");
      await refresh();
      setToast({ kind: "ok", message: "Logo removed" });
    } catch (err) {
      setToast({ kind: "err", message: errorMessage(err, "Couldn't remove logo") });
    }
  };

  const signOut = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    navigate("/sign-in");
  };

  const dirty =
    displayName.trim() !== parish.display_name || accentColor !== parish.accent_color;

  return (
    <main className="page page--settings">
      <p className="back-link">
        <Link to="/">← Back to dashboard</Link>
      </p>

      <h1 className="settings__title">Settings</h1>

      <section className="settings__section" aria-labelledby="parish-identity">
        <h2 id="parish-identity" className="settings__section-title">
          How your parish appears to volunteers
        </h2>
        <p className="settings__section-lede">
          These settings control your parish's logo, name, and accent color
          on the help center and any other pages volunteers see. They
          don't affect Sacristy AI's own interface.
        </p>

        <div className="settings__field">
          <label className="settings__label" htmlFor="display-name">
            Display name
          </label>
          <input
            id="display-name"
            type="text"
            className="settings__input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120}
          />
        </div>

        <div className="settings__field">
          <label className="settings__label" htmlFor="accent-color">
            Accent color
          </label>
          <div className="settings__accent-row">
            <input
              id="accent-color"
              type="color"
              className="settings__color"
              value={accentColor}
              onChange={onAccentChange}
            />
            <input
              type="text"
              className="settings__input settings__input--hex"
              value={accentColor}
              onChange={onAccentChange}
              maxLength={7}
              spellCheck={false}
              aria-label="Accent color hex value"
            />
            <span className="settings__accent-preview">
              <button
                type="button"
                className="settings__accent-sample"
                tabIndex={-1}
                style={
                  isHexColor(accentColor)
                    ? {
                        background: accentColor,
                        color: accentContrastFg(accentColor),
                        borderColor: accentColor,
                      }
                    : undefined
                }
              >
                Sample button
              </button>
            </span>
          </div>
        </div>

        <div className="settings__field">
          <span className="settings__label">Logo</span>
          <div className="settings__logo-preview">
            {parish.logo_url ? (
              <img
                src={resolveApiUrl(parish.logo_url)}
                alt={`${parish.display_name} logo`}
                className="settings__logo-img"
              />
            ) : (
              <span className="settings__logo-empty">No logo uploaded yet.</span>
            )}
          </div>
          <div className="settings__logo-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_LOGO_MIMES}
              onChange={onFile}
              className="settings__file-input"
            />
            <button
              type="button"
              className="btn"
              onClick={() => fileInputRef.current?.click()}
            >
              {parish.logo_url ? "Replace logo" : "Upload logo"}
            </button>
            {parish.logo_url ? (
              <button type="button" className="btn btn--ghost" onClick={removeLogo}>
                Remove logo
              </button>
            ) : null}
          </div>
          <p className="settings__hint">
            PNG, SVG, or JPG. Max 500 KB and 1000 × 1000 pixels.
          </p>
        </div>

        <div className="settings__field">
          <span className="settings__label">Public schedule link</span>
          <p className="settings__hint">
            Share this with the parish — anyone with the link can view the schedule, no login
            needed. It's deliberately unguessable and hidden from search engines, so it's only as
            public as you make it.
          </p>
          {scheduleUrl ? (
            <>
              <div className="settings__link-row">
                <input
                  type="text"
                  className="sign-in__input settings__link-input"
                  value={scheduleUrl}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  aria-label="Public schedule link"
                />
                <button type="button" className="btn" onClick={copyScheduleUrl}>
                  Copy
                </button>
                <a className="btn btn--ghost" href={scheduleUrl} target="_blank" rel="noreferrer">
                  Open
                </a>
              </div>
              <div className="settings__logo-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={regenerateScheduleLink}
                  disabled={regenerating}
                >
                  {regenerating ? "Generating…" : "Generate a new link"}
                </button>
              </div>
              <p className="settings__hint">
                Generating a new link immediately stops the old one from working. Use it if the
                link has been shared more widely than you intended.
              </p>
            </>
          ) : (
            <p className="settings__hint">
              Not available yet — apply migration 0008, then reload this page.
            </p>
          )}
        </div>

        <div className="settings__save-row">
          <button
            type="button"
            className="btn"
            onClick={save}
            disabled={!dirty || saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </section>

      <section className="settings__section" aria-labelledby="attention-prefs">
        <h2 id="attention-prefs" className="settings__section-title">
          What appears in "Needs your attention"
        </h2>
        <p className="settings__section-lede">
          Uncheck anything you'd rather not see in the attention column on
          the dashboard. Changes save automatically and only apply to this
          browser.
        </p>
        <ul className="settings__toggle-list">
          {TOGGLEABLE_KINDS.map((meta) => {
            const id = `attention-${meta.kind}`;
            return (
              <li key={meta.kind} className="settings__toggle-row">
                <input
                  id={id}
                  type="checkbox"
                  className="settings__toggle-checkbox"
                  checked={attentionPrefs[meta.kind]}
                  onChange={(e) => setAttentionPref(meta.kind, e.target.checked)}
                />
                <label htmlFor={id} className="settings__toggle-label">
                  <span className="settings__toggle-title">{meta.label}</span>
                  <span className="settings__toggle-description">
                    {meta.description}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="settings__section" aria-labelledby="billing">
        <h2 id="billing" className="settings__section-title">
          Billing
        </h2>
        <p className="settings__section-lede">
          Pilot status, plans, and the Stripe billing portal.
        </p>
        <div className="settings__save-row">
          <Link to="/settings/billing" className="btn btn--ghost">
            Manage billing
          </Link>
        </div>
      </section>

      <section className="settings__section" aria-labelledby="adoption">
        <h2 id="adoption" className="settings__section-title">
          Volunteer adoption
        </h2>
        <p className="settings__section-lede">
          How many volunteers come back and check their schedule. Counts only, never individual
          activity.
        </p>
        <div className="settings__save-row">
          <Link to="/adoption" className="btn btn--ghost">
            View adoption
          </Link>
        </div>
      </section>

      <section className="settings__section" aria-labelledby="account">
        <h2 id="account" className="settings__section-title">
          Account
        </h2>
        <div className="settings__field">
          <span className="settings__label">Signed in as</span>
          <p className="settings__static">{session.email || "—"}</p>
        </div>
        <div className="settings__save-row">
          <button type="button" className="btn btn--ghost" onClick={signOut}>
            Sign out
          </button>
        </div>
      </section>

      {toast ? (
        <div
          className={`toast toast--${toast.kind}`}
          role={toast.kind === "err" ? "alert" : "status"}
        >
          {toast.message}
        </div>
      ) : null}
    </main>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
